import { isNullOrUndefined } from '@/utils/is-null-or-undefined';

import {
  MIN_PER_HOUR,
  NO_VALUE,
  PSI_GAUGE_SOURCE_UNITS,
  PSI_OUTPUT_LABELS,
  SEC_PER_MIN,
  SUPPORTED_UNITS,
} from './unit-formatter.constants';
import { FormattedUnitLabels, UnitValue } from './unit-formatter.types';

/**
 * Scales a boolean value to a given true or false value.
 * @param rawValue - The value to scale.
 * @param scaledTrueVal - The value to return if the value is true.
 * @param scaledFalseVal - The value to return if the value is false.
 * @returns The scaled value.
 */
export const scaleBooleanValue = <T>(
  rawValue: boolean | string | number | null | undefined,
  scaledTrueVal: T,
  scaledFalseVal: T
): T => {
  if (rawValue === true || ['true', 'on'].includes(String(rawValue).toLowerCase())) {
    return scaledTrueVal;
  }
  return scaledFalseVal;
};

/**
 * Converts a string, number, or boolean to a number.
 * @param rawValue - The value to convert.
 * @returns The converted number or null if it failed.
 */
export const toNumber = (rawValue: string | number | boolean | null | undefined): number | null => {
  if (isNullOrUndefined(rawValue)) return null;

  switch (typeof rawValue) {
    case 'string': {
      const value = parseFloat(rawValue);
      if (isNaN(value)) return null;

      return value;
    }
    case 'number':
      return rawValue;
    case 'boolean':
    default:
      return null;
  }
};

/**
 * Formats a number to a given precision.
 * @param rawValue - The value to format.
 * @param precision - The precision to format the number to.
 * @returns The formatted number or null if it failed.
 */
export const formatNumber = (
  rawValue: string | number | boolean | null | undefined,
  precision: number = 2
): number | null => {
  const value = toNumber(rawValue);
  if (value == null) return null;

  return +value.toFixed(precision);
};

/**
 * Converts a number to an integer.
 * @param num - The number to convert.
 * @returns The integer value.
 */
const floatToInt = (num: any) => {
  // converting to number before doing replace to handle numbers in scientific notation
  const number = Number(num);
  return Number(number.toString().replace('.', ''));
};

/**
 * Gets the length of the decimal part of a number.
 * @param num - The number to get the length of the decimal part of.
 * @returns The length of the decimal part of the number.
 */
const digitLength = (num: any) => {
  // converting to number before doing replace to handle numbers in scientific notation
  const number = Number(num);
  return (number.toString().split('.')[1] || '').length;
};

/**
 * Truncates a number to a given precision.
 * @param num - The number to truncate.
 * @param precision - The precision to truncate the number to.
 * @returns The truncated number.
 */
const truncate = (num: any, precision: number) => {
  return parseFloat(Number(num).toPrecision(precision));
};

/**
 *  fpMultiply - floating point multiplication
 *
 *  multiply, maintaining precision
 * for example in JS: .585 * 60 => 35.099999999999994
 * but, fpMultiply(.585, 60) => 35.1
 *
 * @param a - The first number to multiply.
 * @param b - The second number to multiply.
 * @returns The multiplied number.
 */
export const fpMultiply = (a: any, b: any) => {
  const aInt = floatToInt(a);
  const bInt = floatToInt(b);
  const base = digitLength(a) + digitLength(b);

  return (aInt * bInt) / Math.pow(10, base);
};

/**
 *  fpDivide - floating point division
 *
 *  divide, maintaining precision
 * for example in JS: 35.1 / 60 => 0.5850000000000001
 * but, fpDivide(35.1, 60) => .585
 *
 * @param a - The first number to divide.
 * @param b - The second number to divide.
 * @returns The divided number.
 */
export const fpDivide = (a: any, b: any) => {
  const aInt = floatToInt(a);
  const bInt = floatToInt(b);
  const base = digitLength(b) - digitLength(a);
  const precision = truncate(Math.pow(10, base), 8);

  return fpMultiply(aInt / bInt, precision);
};

/**
 * Checks if a value is numeric.
 * @param num - The value to check.
 * @returns True if the value is numeric, false otherwise.
 */
export const isNumeric = (num: any) => {
  return (
    (typeof num === 'number' && !isNaN(num)) || (typeof num === 'string' && !isNaN(parseFloat(num)))
  );
};

/**
 * Converts a string to a number and scales it by a given factor.
 * @param value - The string to convert.
 * @param scale - The factor to scale the number by.
 * @returns The scaled number or null if it failed.
 */
export const floatHandler = (value: string, scale: number) => {
  let v = parseFloat(value);
  if (isNaN(v)) return null;
  if (scale) {
    v /= scale;
  }
  v = +v.toFixed(2);
  return v;
};

/**
 * Converts a UnitValue to a FormattedUnitLabels object.
 * @param unitValue - The UnitValue to convert.
 * @returns The FormattedUnitLabels object.
 */
export const unitValueToFormattedUnitLabels = (unitValue: UnitValue): FormattedUnitLabels => {
  const unitsIsEmpty = isNullOrUndefined(unitValue.units) || unitValue.units === '';
  const valueIsEmpty = isNullOrUndefined(unitValue.value);

  const formattedValue = valueIsEmpty ? NO_VALUE : unitValue.value;
  const formattedUnits = unitsIsEmpty || valueIsEmpty ? '' : unitValue.units;

  return {
    valueLabel: `${formattedValue}`,
    unitsLabel: `${formattedUnits}`,
    valueUnitsLabel: `${formattedValue}${unitsIsEmpty || valueIsEmpty ? '' : ' ' + formattedUnits}`,
  };
};

/**
 * Converts seconds to a duration string in the format "HH:MM"
 * @param seconds - The number of seconds to convert
 * @returns The duration string in the format "HH:MM"
 */
/**
 * Converts seconds to a duration string in the format "H:MM"
 * Hours are displayed without leading zeros, minutes with leading zeros.
 * @param seconds - The number of seconds to convert
 * @returns The duration string in the format "H:MM" (e.g., "0:00", "1:23", "100:05")
 * @example
 * secondsToDuration(0) // Returns: "0:00"
 * secondsToDuration(3661) // Returns: "1:01"
 * secondsToDuration(36000) // Returns: "10:00"
 */
export const secondsToDuration = (
  seconds: number,
  hoursWithLeadingZero: boolean = false
): string => {
  const secInHr = SEC_PER_MIN * MIN_PER_HOUR;
  const hours = Math.floor(seconds / secInHr);
  const minutes = Math.floor((seconds % secInHr) / SEC_PER_MIN);
  return `${hoursWithLeadingZero ? hours.toString().padStart(2, '0') : hours}:${minutes.toString().padStart(2, '0')}`;
};

/**
 * Converts a duration string in the format "H:MM" to seconds.
 * @param duration - The duration string to convert (e.g., "1:30", "0:05", "100:00")
 * @returns The number of seconds, or null if the format is invalid
 * @example
 * durationToSeconds("0:00") // Returns: 0
 * durationToSeconds("1:01") // Returns: 3660
 * durationToSeconds("10:00") // Returns: 36000
 */
export function durationToSeconds(
  duration: string | number | boolean | null | undefined
): number | null {
  if (isNullOrUndefined(duration) || typeof duration !== 'string') return null;

  const parts = duration.split(':');
  if (parts.length !== 2) return null;

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  if (isNaN(hours) || isNaN(minutes)) return null;

  return hours * MIN_PER_HOUR * SEC_PER_MIN + minutes * SEC_PER_MIN;
}

/**
 * Returns true when source or display units indicate psig-inhg composite pressure mode.
 */
export function isPsigInhgMode(unitValue: UnitValue, displayUnits?: string): boolean {
  const normalizedSource = unitValue.units?.toLowerCase() ?? '';
  const normalizedDisplay = displayUnits?.toLowerCase() ?? '';

  return (
    normalizedSource === SUPPORTED_UNITS.PSIG_INHG ||
    normalizedDisplay === SUPPORTED_UNITS.PSIG_INHG
  );
}

/**
 * Gets the output units for PSI/PSIG/PSIA values (sign-based PSIG vs inHG).
 * Composite psig-inhg display is handled by formatPsigInhg, not this helper.
 *
 * @param unitValue - The UnitValue to get the output units for.
 * @param displayUnits - The display units to get the output units for.
 * @returns The output units for PSI values.
 */
export const getPSIOutputUnits = (unitValue: UnitValue, displayUnits?: string) => {
  const normalizedSource = unitValue.units?.toLowerCase() ?? '';
  const validPSIUnits = PSI_GAUGE_SOURCE_UNITS.includes(
    normalizedSource as (typeof PSI_GAUGE_SOURCE_UNITS)[number]
  );
  if (!validPSIUnits) return displayUnits;

  const value = toNumber(unitValue.value);
  return value != null && value < 0 ? PSI_OUTPUT_LABELS.INHG : PSI_OUTPUT_LABELS.PSIG;
};
